export interface FreeElement {
  id: string
  type: 'button-outline' | 'button-solid' | 'heading' | 'text'
  content: string
  x: number
  y: number
}

export interface SwissStat {
  value: number
  suffix: string
  label: string
}

export interface SwissService {
  num: string
  title: string
  desc: string
}

export interface SwissWorkImg {
  k: 'img'
  src: string
  t: string
  m: string
  s: string
}

export interface SwissWorkTypeTile {
  k: 'type'
  w: string
  m: string
  v: string
  s: string
}

export interface SwissWorkIdx {
  k: 'idx'
  m: string
  list: string[]
  s: string
}

export type SwissWork = SwissWorkImg | SwissWorkTypeTile | SwissWorkIdx

export interface SwissPageData {
  variant: 'bst' | 'nord' | 'klima' | 'orbita'

  // Brand identity
  brandName: string
  brandFull: string
  city: string
  geo: string

  // Hero
  heroKicker: string
  heroTitle: [string, string, string]
  heroTagline: string
  heroImage: string
  heroCap: string
  heroBtn1: string
  heroBtn2: string
  // Hero element visibility
  showHeroKicker: boolean
  showHeroTitle: [boolean, boolean, boolean]
  showHeroTagline: boolean
  showHeroImage: boolean
  showHeroBtn1: boolean
  showHeroBtn2: boolean
  // Hero button element types
  heroBtn1Type: 'btn-outline' | 'btn-solid' | 'link' | 'heading'
  heroBtn2Type: 'btn-outline' | 'btn-solid' | 'link' | 'heading'

  // Marquee
  marqueeClaim: string

  // Manifest
  foundedYear: string
  manifestText1: string
  manifestText2: string

  // Stats (array of 4)
  stats: SwissStat[]

  // Services (array of 4)
  services: SwissService[]

  // Works / portfolio
  works: SwissWork[]

  // Quote
  quote: string
  quoteAuthor: string

  // Contact
  email: string
  contactNote: string
  address: string[]
  phone: string

  // Free elements (drag-and-place on canvas)
  freeElements: FreeElement[]

  // Section visibility
  showManifest: boolean
  showStats: boolean
  showServices: boolean
  showWorks: boolean
  showQuote: boolean
  showContact: boolean
}

export const DEFAULT_SWISS_DATA: SwissPageData = {
  variant: 'bst',
  brandName: 'BST',
  brandFull: 'Bureau Swiss Type',
  city: 'ZÜRICH',
  geo: '47.37° N — 8.54° E',
  heroKicker: 'Бюро визуальных систем · Цюрих · осн. 1957',
  heroTitle: ['Форма', 'следует', 'системе'],
  heroTagline: 'Мы проектируем идентичности, которые работают как швейцарские механизмы: точно, долго и без единой лишней детали.',
  heroImage: 'https://image.qwenlm.ai/public_source/d14d24eb-3fe0-4b05-843e-79a16061294b/1e98062d5-3a6b-4019-ad0c-4d9a4bd0873f.png',
  heroCap: 'Рис. 01 — Рабочий стол бюро. Модульная сетка, 12 колонок',
  heroBtn1: 'Смотреть работы',
  heroBtn2: 'Начать проект',
  showHeroKicker: true,
  showHeroTitle: [true, true, true],
  showHeroTagline: true,
  showHeroImage: true,
  showHeroBtn1: true,
  showHeroBtn2: true,
  heroBtn1Type: 'btn-outline',
  heroBtn2Type: 'btn-solid',
  marqueeClaim: 'СЕТКА · ТИПОГРАФИКА · СИСТЕМА',
  foundedYear: '1957',
  manifestText1: 'Бюро основано в Цюрихе в 1957 году. С тех пор мы держимся одной идеи: <b>дизайн — это не украшение</b>, а инструмент коммуникации. Каждое решение — от кегля до модуля сетки — подчинено задаче.',
  manifestText2: 'Мы работаем с брендами, которым нужна ясность: музеи, банки, транспортные системы, издательства. Метод неизменен — <b>исследование, прототип, система</b>. Никакой импровизации там, где должна быть конструкция.',
  stats: [
    { value: 67,  suffix: '',    label: 'лет практики' },
    { value: 214, suffix: '',    label: 'проектов в архиве' },
    { value: 16,  suffix: '',    label: 'международных наград' },
    { value: 4,   suffix: '',    label: 'города присутствия' },
  ],
  services: [
    { num: '01', title: 'Айдентика',          desc: 'Знаки, логотипы и полные системы фирменного стиля с руководством на 300+ страниц.' },
    { num: '02', title: 'Типографика и сетки', desc: 'Кастомные гарнитуры, модульные сетки, плакатные и редакционные системы.' },
    { num: '03', title: 'Диджитал-продукты',   desc: 'Сайты и интерфейсы, где сетка и иерархия переносятся из печати в пиксели.' },
    { num: '04', title: 'Арт-дирекшн',        desc: 'Кампании, съёмки и навигационные системы для общественных пространств.' },
  ],
  works: [
    { k: 'img',  src: 'https://image.qwenlm.ai/public_source/d14d24eb-3fe0-4b05-843e-79a16061294b/112e0e4d7-15f5-4ea8-954b-a416507df280.png', t: 'Архив плаката',     m: 'Идентичность · 2024', s: 's7' },
    { k: 'type', w: 'Raster',  m: 'Система сеток',        v: 'acc', s: 's5' },
    { k: 'img',  src: 'https://image.qwenlm.ai/public_source/d14d24eb-3fe0-4b05-843e-79a16061294b/13acb3abe-32ff-4ca1-a380-dfc2aff6ffd1.png', t: 'Печатный цех',      m: 'Типографика · 2023', s: 's5' },
    { k: 'idx',  m: 'Избранные проекты 2019—2025', list: ['Музей дизайна — айдентика', 'Kanton Bank — навигация', 'Helvetia Press — журнал', 'Tram Zürich — кампания'], s: 's7' },
    { k: 'type', w: 'Modul',   m: 'Редакционная сетка',   v: 'ink', s: 's4' },
    { k: 'type', w: 'Signet',  m: 'Знак и эмблема',       v: '',    s: 's8' },
  ],
  quote: 'Мы убираем всё, что мешает сообщению. То, что остаётся, и есть бренд.',
  quoteAuthor: 'Бруно Хасслер — арт-директор бюро',
  email: 'hello@bst.ch',
  contactNote: 'Бриф из двух абзацев важнее презентации на сорок слайдов. Пришлите задачу — вернёмся с планом и сметой в течение суток.',
  address: ['Limmatstrasse 21', '8005 Zürich, Schweiz'],
  phone: '+41 44 000 19 57',
  freeElements: [],
  showManifest: true,
  showStats: true,
  showServices: true,
  showWorks: true,
  showQuote: true,
  showContact: true,
}

export function mergeSwissData(raw: Record<string, unknown>): SwissPageData {
  return {
    ...DEFAULT_SWISS_DATA,
    ...raw,
    heroTitle: (raw.heroTitle as [string, string, string]) ?? DEFAULT_SWISS_DATA.heroTitle,
    showHeroTitle: (raw.showHeroTitle as [boolean, boolean, boolean]) ?? DEFAULT_SWISS_DATA.showHeroTitle,
    stats: (raw.stats as SwissStat[]) ?? DEFAULT_SWISS_DATA.stats,
    services: (raw.services as SwissService[]) ?? DEFAULT_SWISS_DATA.services,
    works: (raw.works as SwissWork[]) ?? DEFAULT_SWISS_DATA.works,
    address: (raw.address as string[]) ?? DEFAULT_SWISS_DATA.address,
  }
}
